import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Truck, Plus, AlertTriangle, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import DeleteRequestButton from "../components/DeleteRequestButton";
import { qtyStep, isCountUom } from "../lib/uom";
import { usePager } from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";

const TODAY = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({
  mode: "regular",
  invoice_date: TODAY(), invoice_no: "", supplier_id: "",
  vehicle_no: "", driver_name: "", transporter: "",
  items: [{ material_id: "", qty: "", packing_boxes: "", value: 0 }],
  remarks: "",
  source_dc_id: "", source_dc_number: "", party_doc_no: "",
  // Wave-D (#7): inward challan mode → records challan_no, defers invoice entry
  challan_no: "",
  // Wave-H follow-up: physical challan date (separate from invoice_date)
  challan_date: TODAY(),
});

export default function GateEntry() {
  const { hasPermission } = useAuth();
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [returnableDcs, setReturnableDcs] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [editTarget, setEditTarget] = useState(null);  // User #1: direct edit before GRN
  const [form, setForm] = useState(EMPTY());

  const load = async () => {
    const [g, s, m, sc, dc] = await Promise.all([api.get("/gate-entries"), api.get("/suppliers"), api.get("/materials"), api.get("/schedules"), api.get("/challans")]);
    setRows(g.data); setSuppliers(s.data); setMaterials(m.data); setSchedules(sc.data);
    setReturnableDcs((dc.data || []).filter((c) => c.type === "returnable" && ["issued", "partially_returned"].includes(c.status)));
  };
  useEffect(() => { load(); }, []);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";

  const filteredMaterials = useMemo(
    () => form.supplier_id ? materials.filter((m) => (m.supplier_ids || []).includes(form.supplier_id)) : [],
    [materials, form.supplier_id]
  );

  const updateItem = (idx, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => {
      if (i !== idx) return it;
      const merged = { ...it, ...patch };
      // Auto-fill value from material master cost when material changes
      if ("material_id" in patch) {
        const m = materials.find((x) => x.id === patch.material_id);
        merged.value = m?.cost ?? 0;
      }
      return merged;
    }),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { material_id: "", qty: "", packing_boxes: "", value: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const variance = (qty, expected) => {
    if (!expected) return null;
    const diff = +qty - expected;
    const pct = Math.abs(diff) / Math.max(expected, 1) * 100;
    if (pct <= 10) return { level: "ok", text: `±${diff}` };
    return { level: diff < 0 ? "short" : "excess", text: `${diff < 0 ? "Short" : "Excess"} ${Math.abs(diff)} (${pct.toFixed(1)}%)` };
  };

  const scheduleFor = (mid) => schedules.find((s) => s.supplier_id === form.supplier_id && s.material_id === mid && s.status === "open");

  const save = async () => {
    // Wave-H: client-side cross-field validation — invoice_date must be on or
    // before today's IST date (server is the final authority).
    const todayIST = TODAY();
    if (form.mode === "regular") {
      if (!form.supplier_id || form.items.some((i) => !i.material_id || !i.qty)) {
        toast.error("Supplier and item rows required"); return;
      }
      if (!(form.invoice_no || "").trim()) { toast.error("Invoice number required for regular Gate Entry"); return; }
      if (!(form.invoice_date || "").trim()) { toast.error("Invoice date required"); return; }
      if (form.invoice_date > todayIST) {
        toast.error("Invoice date cannot be after gate entry date. Supplier issues the invoice before shipping.");
        return;
      }
    } else if (form.mode === "returnable") {
      if (!form.source_dc_id) { toast.error("Source returnable DC required"); return; }
      if (!(form.party_doc_no || "").trim()) { toast.error("Party Doc No. is mandatory for Returnable Return"); return; }
      if (form.items.some((i) => !i.material_id || !i.qty)) { toast.error("All item rows need material and quantity"); return; }
      // Optional invoice_date — but if entered, must be ≤ today
      if (form.invoice_date && form.invoice_date > todayIST) {
        toast.error("Invoice date cannot be after gate entry date.");
        return;
      }
    } else if (form.mode === "inward_challan") {
      // Wave-D (#7): Inward Challan — challan_no mandatory, invoice NOT entered by Security
      if (!form.supplier_id) { toast.error("Supplier is mandatory for Inward Challan"); return; }
      if (!(form.challan_no || "").trim()) { toast.error("Challan number is mandatory for Inward Challan"); return; }
      if (form.items.some((i) => !i.material_id || !i.qty)) { toast.error("All item rows need material and quantity"); return; }
      // Challan date (optional) must be ≤ today
      if (form.challan_date && form.challan_date > todayIST) {
        toast.error("Challan date cannot be after gate entry date.");
        return;
      }
    }
    // Wave-B (#11): box count must be filled — default is now blank, not 1.
    if (form.items.some((i) => !i.packing_boxes || +i.packing_boxes < 1)) {
      toast.error("Box count required (≥1) for every item"); return;
    }
    try {
      const receipt_mode = form.mode === "returnable" ? "returnable_dc" : (form.mode === "inward_challan" ? "inward_challan" : "invoice");
      const payload = {
        ...form,
        receipt_mode,
        // Wave-H: never send invoice_date for inward_challan (Store enters it later)
        invoice_date: form.mode === "inward_challan" ? null : form.invoice_date,
        items: form.items.map((i) => ({ material_id: i.material_id, qty: +i.qty, packing_boxes: +i.packing_boxes, value: +i.value })),
      };
      await api.post("/gate-entries", payload);
      setOpen(false);
      toast.success(
        form.mode === "returnable" ? "Returnable inward recorded" :
        form.mode === "inward_challan" ? "Inward Challan recorded — Store to enter invoice later" :
        "Gate entry recorded"
      );
      setForm(EMPTY());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(rows, 10);

  return (
    <div data-testid="gate-entry-page">
      <PageHeader title="Gate Entry" subtitle="Security station — record every material receipt"
        helpText="Tablet-friendly form. Multi-item supported per gate pass. Total/Invoice Amount has been removed. GRN must be raised within 3 days."
        right={<button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-ge-btn"><Plus size={14}/> New Gate Entry</button>}
      />
      <div className="p-8">
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">GR No.</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Supplier</th>
                <th className="text-right px-4 py-2"># Items</th>
                <th className="text-right px-4 py-2">Total Qty</th>
                <th className="text-left px-4 py-2">Vehicle</th>
                <th className="text-left px-4 py-2">Variance</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} data-testid={`ge-row-${r.number}`}>
                  <td className="px-4 py-2 font-mono text-[#1565C0]">{r.number}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.gate_entry_date || r.receipt_date?.slice(0, 10)}</td>
                  <td className="px-4 py-2">{supName(r.supplier_id)}</td>
                  <td className="px-4 py-2 font-mono text-right">{(r.items || []).length}</td>
                  <td className="px-4 py-2 font-mono text-right font-bold text-[#0D2B5E]" data-testid={`ge-total-qty-${r.number}`}>
                    {fmtQty(r.total_qty != null ? r.total_qty : (r.items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0))}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{r.vehicle_no}</td>
                  <td className="px-4 py-2">{r.variance_flag ? <StatusBadge status={r.variance_flag}/> : <span className="text-[10px] uppercase text-[#2E7D32] tracking-widest">OK</span>}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status}/></td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button onClick={() => setView(r)} className="text-[#1565C0]" data-testid={`view-ge-${r.number}`}><Eye size={14}/></button>
                    {/* User #1: Direct edit while no GRN exists. Once a GRN is
                        raised the backend returns 409 and the user routes via
                        edit-with-approval. */}
                    {r.status === "pending_grn" && hasPermission("gate_entry", "edit") && (
                      <button onClick={() => setEditTarget(r)}
                        className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-[#1565C0]/10 text-[#1565C0] hover:bg-[#1565C0]/20"
                        data-testid={`edit-ge-${r.number}`}>Edit</button>
                    )}
                    <span className="inline-block align-middle"><DeleteRequestButton module="gate_entry" record={r} label={r.number} onDeleted={load}/></span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="9" className="p-10 text-center text-[#0D2B5E]/50">No entries yet.</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>
      </div>

      {open && (
        <Dialog title="New Gate Entry" onClose={() => setOpen(false)} wide>
          {/* Mode toggle — Security chooses between Regular Inward and Returnable Return */}
          <div className="mb-4">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-2">Entry Type *</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["regular", "Regular (Invoice)", "Fresh inward with invoice"],
                ["returnable", "Returnable DC Return", "Return against outward Returnable DC"],
                ["inward_challan", "Inward Challan", "Material on challan; invoice entered later by Store"],
              ].map(([v, l, sub]) => {
                const active = form.mode === v;
                const accent = v === "returnable" ? "#1565C0" : (v === "inward_challan" ? "#E65100" : "#0D2B5E");
                return (
                  <button key={v} type="button" data-testid={`ge-mode-${v}`}
                    onClick={() => setForm({ ...EMPTY(), mode: v, invoice_date: form.invoice_date })}
                    className={`text-left px-4 py-3 rounded-sm border-2 transition ${active ? "bg-white" : "bg-white border-[#EEEEEE] hover:border-[#1565C0]/40"}`}
                    style={active ? { borderColor: accent, backgroundColor: `${accent}0D` } : {}}>
                    <div className={`text-sm font-bold uppercase tracking-widest ${active ? "" : "text-[#0D2B5E]/70"}`}
                      style={active ? { color: accent } : {}}>
                      {active ? "● " : "○ "}{l}
                    </div>
                    <div className="text-[11px] text-[#0D2B5E]/60 mt-0.5">{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[#1565C0]">
              <Truck size={16}/>
              <div className="text-[10px] uppercase tracking-widest font-bold">{form.mode === "returnable" ? "Returnable return — minimal entry · most fields auto-filled from source DC" : "Multi-item supported · Tablet-ready"}</div>
            </div>
            {/* Wave-H: surface the server-stamped Gate Entry Date so the
                Security user can SEE what date is being recorded. */}
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E] bg-[#1565C0]/10 border border-[#1565C0]/30 px-3 py-1 rounded-sm" data-testid="ge-today-date">
              Gate Entry Date · <span className="font-mono text-[#1565C0]">{TODAY()}</span>
            </div>
          </div>
          {form.mode === "regular" ? (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Wave-H Dual Date — invoice_date capped at today (IST), gate_entry_date is server-stamped */}
              <Input label="Invoice Date *" type="date" value={form.invoice_date} onChange={(v) => setForm({ ...form, invoice_date: v })} max={TODAY()} testId="ge-invoice-date"/>
              <Input label="Invoice No. *" value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} testId="ge-invoice"/>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Supplier *</label>
                <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value, items: form.items.map(it => ({ ...it, material_id: "" })) })} data-testid="ge-supplier" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                  <option value="">Select supplier…</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <Input label="Vehicle No." value={form.vehicle_no} onChange={(v) => setForm({ ...form, vehicle_no: v })} testId="ge-vehicle"/>
              <Input label="Driver Name" value={form.driver_name} onChange={(v) => setForm({ ...form, driver_name: v })}/>
              <Input label="Transporter" value={form.transporter} onChange={(v) => setForm({ ...form, transporter: v })}/>
            </div>
          ) : form.mode === "inward_challan" ? (
            <div className="bg-[#E65100]/5 border-2 border-[#E65100]/40 rounded-sm p-4 mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#E65100] mb-2">Inward Challan — invoice not yet received</div>
              <div className="text-[11px] text-[#0D2B5E]/70 mb-3">Security records the challan number now. The Store / GRN user will attach the invoice (number + date) later via the Pending Invoice queue. Auto-escalation to PH/Admin after 7 days (configurable).</div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Challan No. *" value={form.challan_no} onChange={(v) => setForm({ ...form, challan_no: v })} testId="ge-challan-no"/>
                <Input label="Challan Date" type="date" value={form.challan_date} onChange={(v) => setForm({ ...form, challan_date: v })} max={TODAY()} testId="ge-challan-date"/>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Supplier *</label>
                  <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value, items: form.items.map(it => ({ ...it, material_id: "" })) })} data-testid="ge-supplier-ic" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    <option value="">Select supplier…</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <Input label="Vehicle No." value={form.vehicle_no} onChange={(v) => setForm({ ...form, vehicle_no: v })} testId="ge-vehicle-ic"/>
                <Input label="Driver Name" value={form.driver_name} onChange={(v) => setForm({ ...form, driver_name: v })}/>
                <Input label="Transporter" value={form.transporter} onChange={(v) => setForm({ ...form, transporter: v })}/>
              </div>
            </div>
          ) : form.source_dc_id ? (
            <div className="bg-[#F5F5F5] border border-[#EEEEEE] rounded-sm p-3 mb-4 grid grid-cols-3 gap-3 text-xs" data-testid="ge-returnable-autofill">
              <KV k="Party (auto)" v={supName(form.supplier_id)}/>
              <KV k="Source DC (auto)" v={form.source_dc_number}/>
              <KV k="Vehicle (auto)" v={form.vehicle_no || "—"}/>
              <KV k="Driver (auto)" v={form.driver_name || "—"}/>
              <KV k="Transporter (auto)" v={form.transporter || "—"}/>
              <KV k="Mode" v="RETURNABLE INWARD"/>
            </div>
          ) : null}

          {/* Returnable Inward — link to source DC (only in returnable mode) */}
          {form.mode === "returnable" && (
            <div className="bg-[#1565C0]/5 border-2 border-[#1565C0]/40 rounded-sm p-4 mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1565C0] mb-2">Returnable Inward — link to outward DC *</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Source Outward Returnable DC *</label>
                  <select value={form.source_dc_id} onChange={(e) => {
                    const dc = returnableDcs.find((d) => d.id === e.target.value);
                    if (!dc) {
                      setForm({ ...form, source_dc_id: "", source_dc_number: "", supplier_id: "", vehicle_no: "", driver_name: "", transporter: "", items: [{ material_id: "", qty: "", packing_boxes: "", value: 0 }] });
                      return;
                    }
                    // Auto-populate supplier and items from the DC
                    const supByName = suppliers.find((s) => s.name === dc.party_name);
                    const dcItems = (dc.items || []).map((it) => {
                      const mat = materials.find((m) => m.code === (it.description || "").split(" ")[0] || m.name === it.description);
                      return { material_id: mat?.id || "", qty: it.qty || "", packing_boxes: 1, value: mat?.cost ?? it.rate ?? 0 };
                    });
                    setForm({
                      ...form,
                      source_dc_id: dc.id,
                      source_dc_number: dc.number,
                      supplier_id: dc.supplier_id || supByName?.id || form.supplier_id,
                      vehicle_no: dc.vehicle_no || "",
                      driver_name: dc.driver_name || "",
                      transporter: dc.transporter || "",
                      invoice_no: dc.number,  // store DC number as invoice ref
                      items: dcItems.length > 0 ? dcItems : form.items,
                    });
                  }} data-testid="ge-source-dc" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    <option value="">— Select returnable DC —</option>
                    {returnableDcs.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.party_name} ({c.status})</option>)}
                  </select>
                </div>
                <Input label="Party Doc No. (return ref)" value={form.party_doc_no} onChange={(v) => setForm({ ...form, party_doc_no: v })} testId="ge-party-doc-no"/>
              </div>
              {form.source_dc_id && (() => {
                const dc = returnableDcs.find((d) => d.id === form.source_dc_id);
                if (!dc) return null;
                const issuedByMat = {};
                (dc.items || []).forEach((it) => {
                  const mat = materials.find((m) => m.code === (it.description || "").split(" ")[0] || m.name === it.description);
                  if (mat) issuedByMat[mat.id] = (issuedByMat[mat.id] || 0) + (+it.qty || 0);
                });
                const receivedByMat = {};
                (dc.inward_grn_history || []).forEach((h) => {
                  (h.items || []).forEach((x) => {
                    receivedByMat[x.material_id] = (receivedByMat[x.material_id] || 0) + (+x.qty || 0);
                  });
                });
                return (
                  <div className="mt-3 bg-white border border-[#EEEEEE] rounded-sm p-3" data-testid="ge-dc-balance">
                    <div className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-2">Balance per material on this DC</div>
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/50">
                        <tr><th className="text-left">Material</th><th className="text-right">Issued</th><th className="text-right">Received</th><th className="text-right">Balance</th></tr>
                      </thead>
                      <tbody>
                        {Object.entries(issuedByMat).map(([mid, iss]) => {
                          const rec = receivedByMat[mid] || 0;
                          const bal = iss - rec;
                          const m = materials.find((mm) => mm.id === mid);
                          return (
                            <tr key={mid}><td className="py-0.5">{m?.code} — {m?.name}</td><td className="text-right font-mono">{iss}</td><td className="text-right font-mono text-[#2E7D32]">{rec}</td><td className={`text-right font-mono font-bold ${bal === 0 ? "text-[#2E7D32]" : "text-[#E65100]"}`} data-testid={`ge-bal-${m?.code}`}>{bal}</td></tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(dc.inward_grn_history || []).length > 0 && (
                      <div className="mt-2 text-[10px] text-[#0D2B5E]/60">
                        <span className="uppercase tracking-widest font-bold">Returns so far:</span>{" "}
                        {(dc.inward_grn_history || []).map((h, i) => (
                          <span key={i} className="font-mono mr-2">
                            <a href={`/grn?view=${encodeURIComponent(h.ge_number)}`} className="text-[#1565C0] hover:underline" data-testid={`ge-history-link-${h.ge_number}`}>{h.ge_number}</a>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-[#2E7D32] uppercase tracking-widest mt-2">✓ Items auto-loaded from {form.source_dc_number}. On stock-in, source DC will auto-close (full or partial). Quality stage is skipped.</div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="mb-2 flex justify-between items-center">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Items {form.mode === "returnable" && "· auto-loaded · only Qty editable"}</div>
            {form.mode === "regular" && (
              <button onClick={addItem} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="ge-add-item">+ Add item</button>
            )}
          </div>
          <table className="w-full text-sm border border-[#EEEEEE] mb-4">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-2 py-1">Material *</th>
                <th className="text-right px-2 py-1">Qty *</th>
                <th className="text-right px-2 py-1">Boxes</th>
                <th className="text-right px-2 py-1">Rate (master)</th>
                <th className="text-left px-2 py-1">Schedule / Variance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const sched = scheduleFor(it.material_id);
                const v = sched && it.qty ? variance(it.qty, sched.expected_qty) : null;
                const isReturnable = form.mode === "returnable";
                return (
                  <tr key={i}>
                    <td className="p-1">
                      {isReturnable ? (
                        <div className="px-2 py-1 text-xs font-mono text-[#0D2B5E]">{matCode(it.material_id)} — {matName(it.material_id)}</div>
                      ) : (
                        <select value={it.material_id} onChange={(e) => updateItem(i, { material_id: e.target.value })} disabled={!form.supplier_id} data-testid={`ge-item-mat-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm disabled:bg-[#F5F5F5]">
                          <option value="">{form.supplier_id ? "Select…" : "Select supplier first"}</option>
                          {filteredMaterials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="p-1"><input type="number" step={qtyStep(materials.find((m)=>m.id===it.material_id)?.uom)} value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} data-testid={`ge-item-qty-${i}`} className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/></td>
                    <td className="p-1"><input type="number" value={it.packing_boxes} onChange={(e) => updateItem(i, { packing_boxes: e.target.value })} disabled={isReturnable} className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right disabled:bg-[#F5F5F5]"/></td>
                    <td className="p-1"><input type="number" value={it.value} disabled data-testid={`ge-item-rate-${i}`} className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right bg-[#F5F5F5] text-[#0D2B5E]/70"/></td>
                    <td className="p-1 text-xs">
                      {sched ? (() => {
                        // Wave-G #3: compare against REMAINING qty in the
                        // schedule period (expected minus what's already
                        // been received), not the original commitment.
                        const baseline = (sched.remaining_qty != null) ? sched.remaining_qty : sched.expected_qty;
                        const vv = it.qty ? variance(it.qty, baseline) : null;
                        return (
                          <span className={`${vv?.level === "short" || vv?.level === "excess" ? "text-[#E65100]" : "text-[#2E7D32]"} text-[10px] font-mono`}>
                            Expected {sched.expected_qty}{sched.received_so_far ? ` · Recv ${sched.received_so_far}` : ""} · Bal {baseline}{vv ? ` · ${vv.text}` : ""}
                          </span>
                        );
                      })() : it.material_id ? (
                        <span className="text-[#E65100] text-[10px] font-mono">No schedule (unscheduled)</span>
                      ) : "—"}
                    </td>
                    <td className="p-1">{form.mode === "regular" && form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-[#C62828]"><X size={14}/></button>}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {/* Feb-2026 #3 — auto-computed total qty. Read-only sum across
                 all items, displayed in the form footer so security operators
                 can confirm the aggregate before submitting. No override
                 possible — the server recomputes on save. */}
              <tr className="border-t-2 border-[#0D2B5E]/20 bg-[#F5F5F5]/40">
                <td className="px-2 py-1.5 text-right text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/70">Total Qty (auto)</td>
                <td className="px-2 py-1.5 text-right font-mono font-bold text-[#0D2B5E]" data-testid="ge-total-qty">
                  {fmtQty(form.items.reduce((s, it) => s + (Number(it.qty) || 0), 0))}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>

          <Input label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })}/>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-ge-btn">{submitBusy ? "Submitting…" : "Submit Gate Entry"}</button>
          </div>
        </Dialog>
      )}

      {view && (
        <Dialog title={`Gate Entry ${view.number}`} onClose={() => setView(null)} wide>
          <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
            <KV k="Supplier" v={supName(view.supplier_id)}/>
            <KV k="Vehicle" v={view.vehicle_no}/>
            <KV k="Driver" v={view.driver_name || "—"}/>
            {/* Wave-H Dual Date — always show both */}
            <KV k="Gate Entry Date" v={view.gate_entry_date || view.receipt_date?.slice(0, 10) || "—"}/>
            <KV k="Invoice Date" v={view.invoice_date || (view.receipt_mode === "inward_challan" ? <span className="text-[#E65100] font-bold">PENDING</span> : "—")}/>
            <KV k="Invoice No." v={view.invoice_no || (view.receipt_mode === "inward_challan" ? <span className="text-[#E65100] font-bold">PENDING</span> : "—")}/>
            {view.receipt_mode === "inward_challan" && <KV k="Challan No." v={view.challan_no || "—"}/>}
            {view.receipt_mode === "inward_challan" && <KV k="Challan Date" v={view.challan_date || "—"}/>}
            <KV k="Mode" v={(view.receipt_mode || "invoice").replace("_", " ").toUpperCase()}/>
            <KV k="Status" v={view.status}/>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr><th className="text-left px-3 py-1">Material</th><th className="text-right px-3 py-1">Qty</th><th className="text-right px-3 py-1">Boxes</th><th className="text-left px-3 py-1">Variance</th></tr>
            </thead>
            <tbody>
              {(view.items || []).map((it, i) => (
                <tr key={i} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                  <td className="px-3 py-1">{matCode(it.material_id)} — {matName(it.material_id)}</td>
                  <td className="px-3 py-1 font-mono text-right">{fmtQty(it.qty)}</td>
                  <td className="px-3 py-1 font-mono text-right">{it.packing_boxes}</td>
                  <td className="px-3 py-1 text-xs">{it.variance_flag ? <StatusBadge status={it.variance_flag}/> : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Auto-computed total qty across all items on this GE. */}
              <tr className="border-t-2 border-[#0D2B5E]/20 bg-[#F5F5F5]/40">
                <td className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/70">Total Qty (auto)</td>
                <td className="px-3 py-2 font-mono text-right font-bold text-[#0D2B5E]">
                  {fmtQty(view.total_qty != null ? view.total_qty : (view.items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0))}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
          {view.remarks && <div className="text-xs mt-3"><span className="font-bold text-[#0D2B5E]/60">Remarks:</span> {view.remarks}</div>}
        </Dialog>
      )}

      {editTarget && (
        <EditGEDialog ge={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />
      )}
    </div>
  );
}

function EditGEDialog({ ge, onClose, onSaved }) {
  const [materials, setMaterials] = React.useState([]);
  const [form, setForm] = React.useState({
    invoice_no: ge.invoice_no || "",
    invoice_date: ge.invoice_date || "",
    vehicle_no: ge.vehicle_no || "",
    driver_name: ge.driver_name || "",
    transporter: ge.transporter || "",
    challan_no: ge.challan_no || "",
    challan_date: ge.challan_date || "",
    remarks: ge.remarks || "",
    // User msg #667 #3 — items must be fully editable on direct-edit.
    items: (ge.items || []).map((it) => ({
      material_id: it.material_id, qty: it.qty, packing_boxes: it.packing_boxes, value: it.value || 0,
    })),
  });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { api.get("/materials").then((r) => setMaterials(r.data || [])); }, []);

  const matInfo = (mid) => materials.find((m) => m.id === mid) || {};
  const updateItem = (i, patch) => setForm((prev) => ({
    ...prev,
    items: prev.items.map((it, idx) => {
      if (idx !== i) return it;
      const merged = { ...it, ...patch };
      // User msg (Feb 2026 #11) — when material changes, auto-fill the value
      // from the new material's master cost so the line value reflects the
      // newly picked material, not the old one.
      if ("material_id" in patch) {
        const m = materials.find((x) => x.id === patch.material_id);
        merged.value = m?.cost ?? 0;
      }
      return merged;
    }),
  }));
  const addItem = () => setForm({ ...form, items: [...form.items, { material_id: "", qty: "", packing_boxes: "", value: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    // Validate items
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (!it.material_id) { toast.error(`Row ${i + 1}: material required`); return; }
      if (!Number(it.qty) || Number(it.qty) <= 0) { toast.error(`Row ${i + 1}: qty must be > 0`); return; }
      if (!Number(it.packing_boxes) || Number(it.packing_boxes) < 1) { toast.error(`Row ${i + 1}: boxes must be ≥ 1`); return; }
      const uom = matInfo(it.material_id).uom || "";
      if (isCountUom(uom) && Number(it.qty) !== Math.floor(Number(it.qty))) {
        toast.error(`Row ${i + 1}: ${uom} requires whole numbers`); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({ material_id: it.material_id, qty: +it.qty, packing_boxes: +it.packing_boxes, value: +(it.value || 0) })),
      };
      await api.put(`/gate-entries/${ge.id}`, payload);
      toast.success("Gate Entry updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog title={`Quick Edit · ${ge.number}`} onClose={onClose} wide>
      <div className="text-[11px] text-[#0D2B5E]/70 mb-4 bg-[#1565C0]/5 border border-[#1565C0]/20 rounded-sm p-3">
        Direct edit (no approval needed) is allowed only while no GRN has been raised against this Gate Entry. Once a GRN exists, edits require Plant Head / Super Admin approval.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input label="Invoice No." value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} testId="edit-ge-invoice-no"/>
        <Input label="Invoice Date" type="date" value={form.invoice_date} onChange={(v) => setForm({ ...form, invoice_date: v })} max={ge.gate_entry_date || undefined} testId="edit-ge-invoice-date"/>
        <Input label="Vehicle No." value={form.vehicle_no} onChange={(v) => setForm({ ...form, vehicle_no: v })} testId="edit-ge-vehicle"/>
        <Input label="Driver Name" value={form.driver_name} onChange={(v) => setForm({ ...form, driver_name: v })}/>
        <Input label="Transporter" value={form.transporter} onChange={(v) => setForm({ ...form, transporter: v })}/>
        {ge.receipt_mode === "inward_challan" && <Input label="Challan No." value={form.challan_no} onChange={(v) => setForm({ ...form, challan_no: v })}/>}
        {ge.receipt_mode === "inward_challan" && <Input label="Challan Date" type="date" value={form.challan_date} onChange={(v) => setForm({ ...form, challan_date: v })} max={ge.gate_entry_date || undefined}/>}
        <div className="col-span-2">
          <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Remarks</label>
          <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" rows={2}/>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1 flex items-center justify-between">
          <span>Items</span>
          <button onClick={addItem} className="text-[#1565C0] text-[10px] uppercase tracking-widest font-bold" data-testid="edit-ge-add-row">+ Add row</button>
        </div>
        <table className="w-full text-sm border border-[#EEEEEE]">
          <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
            <tr>
              <th className="text-left px-2 py-1">Material</th>
              <th className="text-right px-2 py-1">UOM</th>
              <th className="text-right px-2 py-1">Qty</th>
              <th className="text-right px-2 py-1">Boxes</th>
              <th className="text-right px-2 py-1">Value</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => {
              const m = matInfo(it.material_id);
              const uom = m.uom || "";
              const step = qtyStep(uom);
              return (
                <tr key={i}>
                  <td className="p-1">
                    <select value={it.material_id} onChange={(e) => updateItem(i, { material_id: e.target.value })} data-testid={`edit-ge-mat-${i}`}
                      className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm">
                      <option value="">Select…</option>
                      {materials.map((mm) => <option key={mm.id} value={mm.id}>{mm.code} — {mm.name}</option>)}
                    </select>
                  </td>
                  <td className="p-1 text-right text-xs font-mono text-[#0D2B5E]/60">{uom || "—"}</td>
                  <td className="p-1">
                    <input type="number" min="0" step={step} value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })}
                      className="w-28 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right" data-testid={`edit-ge-qty-${i}`}/>
                  </td>
                  <td className="p-1">
                    <input type="number" min="1" step="1" value={it.packing_boxes} onChange={(e) => updateItem(i, { packing_boxes: e.target.value })}
                      className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right" data-testid={`edit-ge-boxes-${i}`}/>
                  </td>
                  <td className="p-1">
                    <input type="number" min="0" value={it.value} onChange={(e) => updateItem(i, { value: e.target.value })}
                      className="w-28 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/>
                  </td>
                  <td className="p-1">{form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-[#C62828] text-xs">×</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#0D2B5E]/60 px-4 py-2">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="edit-ge-save"
          className="text-xs uppercase tracking-widest font-bold text-white bg-[#1565C0] hover:bg-[#0D2B5E] disabled:bg-[#BBB] px-4 py-2 rounded-sm">
          {busy ? "Saving…" : "Save Edit"}
        </button>
      </div>
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-sm font-medium text-[#0D2B5E]">{v}</div>
    </div>
  );
}
